/**
 * @zendir/ui - Input Component
 *
 * Text input field following Astro UX Design System with Zendir purple accents.
 *
 * Astro UX Compliance:
 * - Stacked inputs left-justified
 * - Consistent spacing
 * - Help text for validation (not placeholder)
 * - Required/optional indicators
 * - States: default, hover, focus, invalid, disabled
 *
 * Zendir Enhancements:
 * - Purple focus glow
 * - Smooth transitions
 * - Modern glassmorphism effects
 *
 * @example
 * ```tsx
 * <Input label="Spacecraft ID" placeholder="Enter ID..." />
 * <Input label="Password" type="password" />
 * <Input label="Search" icon={<Icon name="search" />} />
 * <Input label="Email" error="Invalid email format" />
 * ```
 */

import React, { memo, forwardRef, useState, useId } from "react";
import { useTheme } from "../../theme";
import { classNames, safeAccentText, type StatusLevel } from "../../utils";
import { useBreakpoint } from "../layout/useBreakpoint";

export type InputSize = "small" | "medium" | "large";

/** Label placement relative to the input field */
export type LabelPlacement = "above" | "outlined";

export interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  /** Label text or ReactNode (supports rich content like tooltip icons) */
  label?: React.ReactNode;
  /** Helper text below input */
  helperText?: string;
  /** Error message (shows error state) */
  error?: string;
  /** Size variant */
  size?: InputSize;
  /** Status for styling */
  status?: StatusLevel;
  /** Icon at start of input */
  icon?: React.ReactNode;
  /** Icon at end of input */
  iconEnd?: React.ReactNode;
  /** Full width */
  fullWidth?: boolean;
  /** Required indicator */
  required?: boolean;
  /** Optional indicator (shows "optional" text) */
  optional?: boolean;
  /** Custom className */
  className?: string;
  /**
   * Label placement style.
   * - `'outlined'` — label sits on the input border (notched/Material-style). **Default.**
   * - `'above'`    — label sits above the input with a gap (classic stack).
   */
  labelPlacement?: LabelPlacement;
}

export const Input = memo(
  forwardRef<HTMLInputElement, InputProps>(function Input(
    {
      label,
      helperText,
      error,
      size = "medium",
      status,
      icon,
      iconEnd,
      fullWidth = false,
      required,
      optional,
      disabled,
      className = "",
      id: propId,
      style,
      onFocus,
      onBlur,
      labelPlacement = "outlined",
      type = "text",
      autoComplete,
      inputMode,
      onPaste: onPasteUser,
      ...rest
    },
    ref,
  ): React.ReactElement {
    // Mobile: email keyboard when type is email (industry standard).
    const resolvedInputMode =
      inputMode ?? (type === "email" ? "email" : undefined);
    const { tokens, theme } = useTheme();
    const { isMobile } = useBreakpoint();
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);
    const generatedId = useId();
    const inputId = propId ?? generatedId;

    // Trim pasted content for password, email, and username (industry standard: avoid auth/validation failures).
    const shouldTrimOnPaste =
      type === "password" ||
      type === "email" ||
      autoComplete === "email" ||
      autoComplete === "username";
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (shouldTrimOnPaste) {
        const pasted = e.clipboardData.getData("text");
        const trimmed = pasted.trim();
        if (trimmed !== pasted) {
          e.preventDefault();
          const input = e.currentTarget;
          const start = input.selectionStart ?? 0;
          const end = input.selectionEnd ?? 0;
          const cur = (rest as { value?: string }).value ?? input.value ?? "";
          const newValue = cur.slice(0, start) + trimmed + cur.slice(end);
          rest.onChange?.({
            target: { ...input, value: newValue },
          } as React.ChangeEvent<HTMLInputElement>);
          requestAnimationFrame(() => {
            input.setSelectionRange(
              start + trimmed.length,
              start + trimmed.length,
            );
          });
        }
      }
      onPasteUser?.(e);
    };

    // Trim on blur for email/username so " user@example.com " becomes "user@example.com" when leaving the field.
    const shouldTrimOnBlur =
      type === "email" ||
      autoComplete === "email" ||
      autoComplete === "username";
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (shouldTrimOnBlur) {
        const input = e.currentTarget;
        const cur = input.value;
        const trimmed = cur.trim();
        if (trimmed !== cur && rest.onChange) {
          rest.onChange({
            target: { ...input, value: trimmed },
          } as React.ChangeEvent<HTMLInputElement>);
        }
      }
      setFocused(false);
      onBlur?.(e);
    };

    // Transparent theme detection (purple-hue transparent bg)
    const isTransparentTheme =
      theme === "transparent" ||
      theme === "transparent-bold" ||
      theme === "transparent-minimal";
    const _transparentInputBg = tokens.colors.interactive.transparentInputBg;

    const hasError = !!error;
    const isOutlined = labelPlacement === "outlined";

    // Determine border color based on state
    const getBorder = () => {
      if (hasError) return tokens.borders.input.error;
      if (status)
        return `${tokens.borders.width.thin} solid ${tokens.colors.status[status]}`;
      if (focused) return tokens.borders.input.focus;
      if (hovered && !disabled) return tokens.borders.input.hover;
      return tokens.borders.input.default;
    };

    const sizeConfig = {
      small: {
        padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
        fontSize: tokens.typography.fontSize.xs,
        height: tokens.elementSize.sm,
        iconSize: 14,
        labelSize: "0.6875rem",
        outlinedLabelSize: "0.625rem",
        inputPaddingLeft: 10,
      },
      medium: {
        padding: `${tokens.spacing.sm} ${tokens.spacing.smd}`,
        fontSize: tokens.typography.fontSize.sm,
        height: tokens.elementSize.md,
        iconSize: 18,
        labelSize: "0.75rem",
        outlinedLabelSize: "0.6875rem",
        inputPaddingLeft: 12,
      },
      large: {
        padding: `${tokens.spacing.smd} ${tokens.spacing.md}`,
        fontSize: tokens.typography.fontSize.base,
        height: tokens.elementSize.lg,
        iconSize: 20,
        labelSize: "0.875rem",
        outlinedLabelSize: "0.75rem",
        inputPaddingLeft: 16,
      },
    };

    const config = sizeConfig[size];
    const controlHeight = isMobile ? "44px" : config.height;

    // Focus glow effect
    const focusGlow =
      focused && !hasError
        ? tokens.borders.focusRing.default
        : hasError && focused
          ? `0 0 0 3px ${tokens.colors.status.critical}20`
          : "none";

    // Background for the outlined label "notch" (must be opaque to mask the border)
    const labelBg = isTransparentTheme
      ? tokens.colors.background.base
      : tokens.colors.background.surface;

    // Shared label content (text + required/optional indicators)
    const labelContent = (
      <>
        {label}
        {required && (
          <span
            style={{ color: tokens.colors.status.critical, marginLeft: "2px" }}
          >
            *
          </span>
        )}
        {optional && (
          <span
            style={{
              color: tokens.colors.text.tertiary,
              fontWeight: 400,
              fontStyle: "italic",
            }}
          >
            {" "}
            (optional)
          </span>
        )}
      </>
    );

    return (
      <div
        className={classNames("zendir-input-wrapper", className)}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: tokens.spacing.xs,
          width: fullWidth ? "100%" : "auto",
          minWidth: fullWidth ? undefined : isMobile ? "100%" : "200px",
          ...style,
        }}
      >
        {/* Label: 'above' variant — sits above the input with gap */}
        {label && !isOutlined && (
          <label
            htmlFor={inputId}
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.xs,
              fontSize: config.labelSize,
              fontWeight: 500,
              color: tokens.colors.text.secondary,
              letterSpacing: "0.01em",
            }}
          >
            {labelContent}
          </label>
        )}

        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {/* Label: 'outlined' variant — positioned on the input border */}
          {label && isOutlined && (
            <label
              htmlFor={inputId}
              style={{
                position: "absolute",
                top: 0,
                left: icon ? "34px" : `${config.inputPaddingLeft - 6}px`,
                transform: "translateY(-50%)",
                backgroundColor: labelBg,
                padding: `0 ${tokens.spacing.xs}`,
                fontSize: config.outlinedLabelSize,
                fontWeight: 500,
                color: focused
                  ? hasError
                    ? tokens.colors.status.critical
                    : tokens.colors.accent.primary
                  : hasError
                    ? tokens.colors.status.critical
                    : tokens.colors.text.secondary,
                letterSpacing: "0.01em",
                zIndex: 1,
                pointerEvents: "none",
                transition: "color 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
              }}
            >
              {labelContent}
            </label>
          )}

          {icon && (
            <span
              style={{
                position: "absolute",
                left: tokens.spacing.smd,
                display: "flex",
                alignItems: "center",
                color: focused
                  ? safeAccentText(tokens.colors.accent.primary)
                  : tokens.colors.text.tertiary,
                pointerEvents: "none",
                transition: tokens.animation.fast,
              }}
            >
              {icon}
            </span>
          )}

          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            required={required}
            aria-invalid={hasError}
            aria-describedby={
              helperText || error ? `${inputId}-helper` : undefined
            }
            onFocus={(e) => {
              setFocused(true);
              onFocus?.(e);
            }}
            onBlur={handleBlur}
            onPaste={handlePaste}
            type={type}
            autoComplete={autoComplete}
            inputMode={resolvedInputMode}
            style={{
              width: "100%",
              height: controlHeight,
              padding: config.padding,
              paddingLeft: icon ? "40px" : config.padding,
              paddingRight: iconEnd ? "40px" : config.padding,
              fontSize: config.fontSize,
              fontFamily: tokens.typography.fontFamily.primary,
              color: tokens.colors.text.primary,
              backgroundColor: isTransparentTheme
                ? "rgba(10, 15, 25, 0.4)"
                : tokens.colors.background.surface,
              border: getBorder(),
              borderRadius: tokens.borderRadius.md,
              outline: "none",
              transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: disabled ? "not-allowed" : "text",
              opacity: disabled ? 0.5 : 1,
              boxShadow: focusGlow,
            }}
            {...rest}
          />

          {iconEnd && (
            <span
              style={{
                position: "absolute",
                right: tokens.spacing.smd,
                display: "flex",
                alignItems: "center",
                color: tokens.colors.text.tertiary,
                cursor: "pointer",
              }}
            >
              {iconEnd}
            </span>
          )}
        </div>

        {(helperText || error) && (
          <span
            id={`${inputId}-helper`}
            role={hasError ? "alert" : undefined}
            style={{
              fontSize: "0.6875rem",
              fontWeight: hasError ? 500 : 400,
              color: hasError
                ? tokens.colors.status.critical
                : tokens.colors.text.tertiary,
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.xs,
            }}
          >
            {hasError && (
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill={tokens.colors.status.critical}
                aria-hidden="true"
                focusable="false"
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
              </svg>
            )}
            {error || helperText}
          </span>
        )}
      </div>
    );
  }),
);

export default Input;
